import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  changeEmail,
  changePassword,
  communicationSettings,
  createDeletionRequest,
  emailStatus,
  exchangeStaffClientOneTimeCode,
  fetchDeletionRequest,
  fetchUser,
  forgotEmail,
  phoneStatus,
  requestPasswordResetCode,
  requestSignInCode,
  signIn,
  signInWithCode,
  signUp,
  updateCommunicationSettings,
  updateUser,
  type UserInfo,
} from "@/services/user";
import { isSetAndNotEmpty } from "@/utils/helpers";

export const UserQueryKey = ["user"];

const DELETION_REQUEST_QUERY_KEY = "deletion-request";

export const useEmailStatusMutation = () =>
  useMutation({
    mutationFn: emailStatus,
  });

export const useRequestSignInCodeMutation = () =>
  useMutation({
    mutationFn: requestSignInCode,
  });

export const useRequestPasswordResetCodeMutation = () =>
  useMutation({
    mutationFn: requestPasswordResetCode,
  });

export const useSignInMutation = () => useMutation({ mutationFn: signIn });

export const useSignInWithCodeMutation = () =>
  useMutation({ mutationFn: signInWithCode });

export const useSignUpMutation = () => useMutation({ mutationFn: signUp });

export const useChangePasswordMutation = () =>
  useMutation({ mutationFn: changePassword });

export const useChangeEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changeEmail,
    onSuccess: (data) => {
      queryClient.setQueryData(UserQueryKey, data);
    },
  });
};

export const useUserQuery = (token?: string) => {
  return useQuery({
    queryKey: UserQueryKey,
    queryFn: isSetAndNotEmpty(token) ? fetchUser : skipToken,
  });
};

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      queryClient.setQueryData(UserQueryKey, data);
    },
  });
};

export const useCreateDeletionRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDeletionRequest,
    onSuccess: (res) => {
      queryClient.setQueryData([DELETION_REQUEST_QUERY_KEY], res);
    },
  });
};

export const useDeletionRequestQuery = () =>
  useQuery({
    queryKey: [DELETION_REQUEST_QUERY_KEY],
    queryFn: fetchDeletionRequest,
  });

export const useSetUserHasCardOnFile = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.setQueryData<UserInfo>(UserQueryKey, (data) =>
      data
        ? {
            ...data,
            has_card_on_file: true,
          }
        : data,
    );
  };
};

export const useStaffClientOneTimeCodeMutation = () => {
  return useMutation({
    mutationFn: exchangeStaffClientOneTimeCode,
  });
};

export const useForgotEmailMutation = () => {
  return useMutation({
    mutationFn: forgotEmail,
  });
};

export function usePhoneStatusMutation() {
  return useMutation({
    mutationFn: phoneStatus,
  });
}

const CommunicationSettingsKey = ["communication-settings"];

export function useCommunicationSettings() {
  return useQuery({
    queryKey: CommunicationSettingsKey,
    queryFn: communicationSettings,
  });
}

export function useUpdateCommunicationSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCommunicationSettings,
    onSuccess: (data) => {
      queryClient.setQueryData(CommunicationSettingsKey, data);
    },
  });
}
